import classcat from "classcat"
import React, { useEffect } from "react"
import { useFormContext } from "react-hook-form"
import { graphql, useFragment } from "react-relay"
import { v4 as uuid } from "uuid"

import commonStyles from "../ProjectBudgetForm.module.css"
import styles from "./OtherExpensesEditor.module.css"

import { OtherExpensesEditor_project$key } from "~/forms/ProjectBudgetForm/OtherExpenses/__generated__/OtherExpensesEditor_project.graphql"

import currency from "~/helpers/CurrencyHelper"
import {
  OtherCostWithCalculations,
  getTotalExpenses,
} from "~/helpers/otherCosts"

import AddButton from "~/common/buttons/AddButton"

import ExpenseBudgetInput from "~/forms/ProjectBudgetForm/ExpenseBudgetInput"
import ExpenseTotalsRow from "~/forms/ProjectBudgetForm/ExpenseTotalsRow"

import { useProjectBudgetFormContext } from "../ProjectBudgetFormProvider"
import Column from "../TableColumn"
import Row from "../TableRow"
import { ProjectBudgetFormData } from "../formTypes"

import OtherExpenseEditRow from "./OtherExpenseEditRow"

type Props = {
  project: OtherExpensesEditor_project$key
}

export const createEmptyExpense = (): OtherCostWithCalculations => ({
  id: `new--${uuid()}`,
  date: new Date(),
  cost: 0,
  charge: 0,
  margin: 0,
  profit: 0,
  name: "",
})

const isEmptyExpense = (expense: OtherCostWithCalculations) => {
  return !expense.name?.trim() && expense.cost === 0 && expense.charge === 0
}

const OtherExpensesEditor = (props: Props) => {
  const { watch, setValue } = useFormContext<ProjectBudgetFormData>()
  const formData = watch()
  const { showNonBillableView } = useProjectBudgetFormContext()

  const expenses = formData.otherExpenses
  const budgetMethod = formData.budgetMethod
  const totalExpenses = getTotalExpenses(expenses)

  // Get expenses budget from roles
  const expenseRole = formData.roles.find((r) => r.name === "Other Expenses")
  const expensesBudget = expenseRole?.estimatedMinutes ?? 0

  const project = useFragment<OtherExpensesEditor_project$key>(
    graphql`
      fragment OtherExpensesEditor_project on projects {
        id
        ...OtherExpenseEditRow_project
      }
    `,
    props.project,
  )

  const canDeleteRow =
    expenses.length > 1 ||
    (expenses.length === 1 && !isEmptyExpense(expenses[0]))

  const showPhaseSelector = budgetMethod !== "ROLE"

  // Ensure there's always at least one expense row (even if empty) when the user is actively working with expenses
  useEffect(() => {
    if (expenses.length === 0 && expenseRole) {
      setValue("otherExpenses", [createEmptyExpense()])
    }
  }, [expenses.length, expenseRole, setValue])

  const handleAdd = () => {
    const newExpenses = [...expenses, createEmptyExpense()]
    setValue("otherExpenses", newExpenses)
  }

  const handleExpensesBudgetUpdate = (value: number) => {
    setValue("expensesBudget", value)

    if (expenseRole) {
      const updatedRoles = [...formData.roles]
      const roleIndex = updatedRoles.findIndex(
        (r) => r.name === "Other Expenses",
      )
      if (roleIndex !== -1) {
        updatedRoles[roleIndex] = {
          ...updatedRoles[roleIndex],
          estimatedMinutes: value,
        }
        setValue("roles", updatedRoles)
      }
    }
  }

  return (
    <>
      {!showNonBillableView && !showPhaseSelector && (
        <div className={styles.expensesHeader}>
          <div className={styles.label}>Expenses Budget</div>
          <ExpenseBudgetInput
            dataTest="expenses-budget_dollars"
            name="expenses-budget_dollars"
            currency={currency}
            onUpdate={handleExpensesBudgetUpdate}
            value={expensesBudget}
            maxWidth={180}
            height={40}
          />
        </div>
      )}
      <div
        className={classcat([
          commonStyles.otherExpenseWrapper,
          {
            [commonStyles.phaseMode]: showPhaseSelector,
          },
        ])}
      >
        <Row heading>
          <Column>Item</Column>
          {showPhaseSelector && <Column>Phase</Column>}
          <Column textAlign="right">Date</Column>
          {!showNonBillableView && (
            <>
              <Column textAlign="right">Margin</Column>
              <Column textAlign="right">Gross Profit</Column>
            </>
          )}
          <Column textAlign="right">Cost</Column>
          {!showNonBillableView && <Column textAlign="right">Charge</Column>}
        </Row>
        <div className={commonStyles.tableBody}>
          {expenses.map((expense, index) => (
            <OtherExpenseEditRow
              key={`expense-${expense.id}`}
              expenseIndex={index}
              project={project}
              canDelete={canDeleteRow}
            />
          ))}
        </div>
      </div>
      <div className={commonStyles.tableWidget}>
        <Row style={{ display: "flex", padding: "10px 0" }}>
          <AddButton text="Add item" onClick={handleAdd} />
        </Row>
      </div>
      <ExpenseTotalsRow
        showNonBillableView={showNonBillableView}
        totalExpenses={totalExpenses}
        title="Total Expenses"
      />
    </>
  )
}

export default OtherExpensesEditor
